import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '../common/Modal';
import { PassportInput } from './modal/PassportInput';
import { GuideSearch } from './modal/GuideSearch';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { useGuidesStore } from '../../stores/guidesStore';
import { useOrganizersStore } from '../../stores/organizersStore';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { Check, Copy } from 'lucide-react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';

const SEARCH_TYPE_KEY = 'pilgrim_search_type_preference';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const AddPilgrimModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage();
  const [identifier, setIdentifier] = useState('');
  const [searchType, setSearchType] = useState<'passport' | 'email'>('passport');
  const [selectedGuideId, setSelectedGuideId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedGuide, setCopiedGuide] = useState('');
  const [organizerId, setOrganizerId] = useState<string | null>(null);
  const [isNotOwner, setIsNotOwner] = useState(false); // New state for ownership toggle

  const { getPilgrimDetails, linkPilgrimToGuide } = usePilgrimGuideStore();
  const { guides, loading: guidesLoading, initialized: guidesInitialized, initializeListener: initializeGuides } = useGuidesStore();
  const { organizers, loading: organizersLoading, initialized: organizersInitialized, fetchOrganizers } = useOrganizersStore();

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    const initializeData = async () => {
      // Initialize guides listener if not already initialized
      if (!guidesInitialized) {
        initializeGuides();
      }

      // Fetch organizers if needed
      if (!organizersInitialized) {
        await fetchOrganizers();
      }
    };

    if (isOpen) {
      initializeData();
    }
  }, [isOpen, guidesInitialized, organizersInitialized, initializeGuides, fetchOrganizers]);

  // Filter guides based on user role
  const availableGuides = useMemo(() => {
    if (userRole === 'admin') {
      return Object.values(guides);
    } else if (userRole === 'organizer' && userId) {
      return Object.values(guides).filter(guide => guide.organizerId === userId);
    }
    return [];
  }, [guides, userRole, userId]);

  const handleSearchTypeChange = (type: 'passport' | 'email') => {
    setSearchType(type);
    localStorage.setItem(SEARCH_TYPE_KEY, type);
  };

  const handleSubmit = async () => {
    if (!identifier.trim()) {
      toast.error(
        searchType === 'passport'
          ? (translations?.addPilgrimModal?.passportError || 'Please enter a passport number')
          : (translations?.addPilgrimModal?.emailError || 'Please enter an email address')
      );
      return;
    }

    if ((userRole === 'admin' || userRole === 'organizer') && !selectedGuideId) {
      toast.error(translations?.addPilgrimModal?.guideError || 'Please select a guide');
      return;
    }

    setIsLoading(true);
    try {
      const pilgrim = await getPilgrimDetails(identifier, searchType, translations);
      if (!pilgrim) {
        toast.error(translations?.addPilgrimModal?.pilgrimNotFound || 'Pilgrim not found');
        return;
      }

      let guideId: string;
      let targetOrganizerId: string;

      if (userRole === 'admin') {
        const guideDoc = await getDoc(doc(firestore, 'Guides', selectedGuideId));
        if (!guideDoc.exists()) {
          throw new Error(translations?.addPilgrimModal?.guideNotFound || 'Guide not found');
        }
        guideId = selectedGuideId;
        targetOrganizerId = guideDoc.data().organizerId;
      } else if (userRole === 'guide' && userId) {
        guideId = userId;
        targetOrganizerId = organizerId!;
      } else if (userRole === 'organizer' && selectedGuideId) {
        guideId = selectedGuideId;
        targetOrganizerId = userId!;
      } else {
        throw new Error(translations?.addPilgrimModal?.unauthorized || 'Unauthorized operation');
      }

      await linkPilgrimToGuide(
        pilgrim.pilgrimId, 
        guideId, 
        targetOrganizerId, 
        translations,
        !isNotOwner // Pass ownership status (inverted since we're tracking "not owner")
      );
      
      toast.success(translations?.addPilgrimModal?.success || 'Pilgrim added successfully');
      handleClose();
    } catch (error: any) {
      console.error('Assignment error:', error);
      toast.error(error.message || translations?.addPilgrimModal?.error || 'An error occurred while adding the pilgrim');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIdentifier('');
    setSelectedGuideId('');
    setSearchType('passport');
    setIsNotOwner(false);
    onClose();
  };

  const handleCopyGuideName = (name: string) => {
    navigator.clipboard.writeText(name);
    setCopiedGuide(name);
    setTimeout(() => setCopiedGuide(''), 2000);
  };

  if (guidesLoading || organizersLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={translations?.addPilgrimModal?.title || 'Add New Pilgrim'}
      width="90vw"
      maxWidth="500px"
    >
      <div className="space-y-6">
        {/* Available Guides Section */}
        {(userRole === 'admin' || userRole === 'organizer') && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-3">
              {translations?.addPilgrimModal?.availableGuides || 'Available Guides'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableGuides.map((guide) => (
                <motion.div
                  key={guide.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-3 rounded-lg shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{guide.name}</span>
                    <button
                      onClick={() => handleCopyGuideName(guide.name)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                    >
                      {copiedGuide === guide.name ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="space-y-2">
          <PassportInput
            value={identifier}
            onChange={setIdentifier}
            isLoading={isLoading}
            searchType={searchType}
            onSearchTypeChange={handleSearchTypeChange}
          />
        </div>

        {/* Guide Search */}
        {(userRole === 'admin' || userRole === 'organizer') && (
          <div className="space-y-2">
            <GuideSearch
              guides={guides}
              selectedGuideId={selectedGuideId}
              onSelectGuide={setSelectedGuideId}
              userRole={userRole}
              userId={userId}
            />
          </div>
        )}

        {/* Not Owner Checkbox - Only for admin */}
        {userRole === 'admin' && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="not-owner"
              checked={isNotOwner}
              onChange={(e) => setIsNotOwner(e.target.checked)}
              className="rounded text-primary-500 focus:ring-primary-500"
            />
            <label htmlFor="not-owner" className="text-sm font-medium text-gray-700">
              {translations?.addPilgrimModal?.notOwner || 'Mark as Not Owner'}
            </label>
          </div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col-reverse md:flex-row gap-3 pt-4"
        >
          <button
            onClick={handleClose}
            className="w-full md:w-auto px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
          >
            {translations?.addPilgrimModal?.cancelButton || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || (userRole !== 'guide' && !selectedGuideId)}
            className={`w-full md:w-auto px-6 py-2 rounded-lg text-white transition-colors ${
              isLoading || (userRole !== 'guide' && !selectedGuideId)
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {isLoading
              ? translations?.addPilgrimModal?.addingButton || 'Adding...'
              : translations?.addPilgrimModal?.addButton || 'Add'}
          </button>
        </motion.div>
      </div>
    </Modal>
  );
};